// frontend/src/App.js
// Updated to include ThemeProvider

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { authContext, getUser } from './auth/auth';
import { updateAgentProfile } from './api';
import { ThemeProvider } from './contexts/ThemeContext';

// Layouts
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Main Pages
import LandingPage from './pages/index';
import LoginPage from './pages/login';
import ForgotPasswordPage from './pages/forgot-password';
import DashboardPage from './pages/dashboard';
import LeadsPage from './pages/admin/leads';
import LeadDetailPage from './pages/lead/[id]';
import ProfilePage from './pages/profile';
import ThankYou from './pages/ThankYou';
import AnnuityWhaleDashboard from './testing/annuity-whale-dashboard.tsx';
import AnnuityWhaleLeadDashboard from './testing/annuity-whale-frontend.tsx';
import AnnuityWhaleEnhancedFrontend from './testing/annuity-whale-enhanced-frontend.tsx';
import BillingPage from './testing/Page/BillingPage';
import PaymentPage from './testing/Page/PaymentPage';
import SalesPage from './testing/Page/SalesPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/index';
import LeadsAdmin from './pages/admin/leads';
import UnbounceLeads from './pages/admin/UnbounceLeads';
import DatabaseLeads from './pages/admin/DatabaseLeads';
import LeadEditPage from './pages/admin/lead';
import AgentsAdmin from './pages/admin/agents';
import SettingsPage from './pages/admin/settings';
import TransactionsAdminPage from './pages/admin/transactions';
import EmailsAdmin from './pages/admin/EmailsAdmin';
import ManageLeadEmails from './pages/ManageLeadEmails';
import ApiMonitor from './pages/admin/ApiMonitor';
import EmailTrackingDashboard from './components/admin/EmailTrackingDashboard';

const App = () => {
  const [user, setUser] = useState(getUser());

  // Update user profile if needed
  useEffect(() => {
    const updateProfile = async () => {
      if (user && !user.isAdmin) {
        try {
          const result = await updateAgentProfile();
          if (result.success) {
            setUser(result.data);
          }
        } catch (error) {
          console.error('Error updating agent profile:', error);
        }
      }
    };
    
    updateProfile();
  }, [user]);

  return (
    <ThemeProvider>
      <authContext.Provider value={{ user, setUser }}>
        <Routes>
          {/* Redirect root to appropriate dashboard based on user role */}
          <Route path="/" element={
            user ? (
              user.isAdmin ? <Navigate to="/admin" /> : <Navigate to="/dashboard" />
            ) : <LandingPage />
          } />
          
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          
          {/* Main Layout Routes */}
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={user ? <DashboardPage /> : <Navigate to="/login" />} />
            <Route path="/leads" element={user ? <LeadsPage /> : <Navigate to="/login" />} />
            <Route path="/lead/:id" element={user ? <LeadDetailPage /> : <Navigate to="/login" />} />
            <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/login" />} />
            <Route path="/thank-you" element={<ThankYou />} />
          </Route>

          {/* Admin Layout Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={user?.isAdmin ? <AdminDashboardPage /> : <Navigate to="/login" />} />
            <Route path="leads" element={user?.isAdmin ? <LeadsAdmin /> : <Navigate to="/login" />} />
            <Route path="database" element={user?.isAdmin ? <DatabaseLeads /> : <Navigate to="/login" />} />
            <Route path="unbounce" element={user?.isAdmin ? <UnbounceLeads /> : <Navigate to="/login" />} />
            <Route path="leads/:id" element={user?.isAdmin ? <LeadEditPage /> : <Navigate to="/login" />} />
            <Route path="agents" element={user?.isAdmin ? <AgentsAdmin /> : <Navigate to="/login" />} />
            <Route path="transactions" element={user?.isAdmin ? <TransactionsAdminPage /> : <Navigate to="/login" />} />
            <Route path="settings" element={user?.isAdmin ? <SettingsPage /> : <Navigate to="/login" />} />
            <Route path="emails" element={user?.isAdmin ? <EmailsAdmin /> : <Navigate to="/login" />} />
            <Route path="emails/:id" element={user?.isAdmin ? <ManageLeadEmails /> : <Navigate to="/login" />} />
            <Route path="email-tracking" element={user?.isAdmin ? <EmailTrackingDashboard /> : <Navigate to="/login" />} />
            <Route path="api-monitor" element={user?.isAdmin ? <ApiMonitor /> : <Navigate to="/login" />} />
          </Route>

          {/* Testing Routes */}
          <Route path="/annuity-whale-dashboard" element={<AnnuityWhaleDashboard />} />
          <Route path="/annuity-whale-frontend" element={<AnnuityWhaleLeadDashboard />} />
          <Route path="/annuity-whale-enhanced-frontend" element={<AnnuityWhaleEnhancedFrontend />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/sales" element={<SalesPage />} />
        </Routes>
      </authContext.Provider>
    </ThemeProvider>
  );
};

export default App;